import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useBlocker } from '@tanstack/react-router';
import type { NavigationRequestOptions } from '../types/navigation';

type UseNavigationGuardProps = {
  hasUnsavedChanges: boolean;
  onBatchSave: () => Promise<void>;
  onCancelChanges: () => void;
};

type NavigationOptions = NavigationRequestOptions;

type UseNavigationGuardReturn = {
  isUnsavedChangesDialogOpen: boolean;
  setIsUnsavedChangesDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pendingNavigation: string | -1 | null;
  setPendingNavigation: React.Dispatch<React.SetStateAction<string | -1 | null>>;
  saveBeforeNavigateError: string | null;
  setSaveBeforeNavigateError: React.Dispatch<React.SetStateAction<string | null>>;
  requestNavigation: (destination: string | -1, options?: NavigationOptions) => void;
  handleSaveAndNavigate: () => Promise<void>;
  handleDiscardAndNavigate: () => void;
  handleCancelNavigation: () => void;
};

/** Blocks navigation when there are unsaved changes; provides save / discard / cancel dialog control */
export const useNavigationGuard = ({
  hasUnsavedChanges,
  onBatchSave,
  onCancelChanges,
}: UseNavigationGuardProps): UseNavigationGuardReturn => {
  const navigate = useNavigate();

  const normalizePath = useCallback((path: string) => {
    const pathnameOnly = path.split('?')[0].split('#')[0];
    return pathnameOnly.endsWith('/') && pathnameOnly !== '/'
      ? pathnameOnly.slice(0, -1)
      : pathnameOnly;
  }, []);

  const markFastReturnIfNeeded = useCallback(
    (destination: string | -1, options?: NavigationOptions) => {
      if (!options?.preferCachedOnMount || destination === -1) return;
      try {
        const normalizedDestination = normalizePath(destination);
        sessionStorage.setItem(`GRID_FAST_RETURN:${normalizedDestination}`, '1');
      } catch {
        /* ignore */
      }
    },
    [normalizePath]
  );

  const [isUnsavedChangesDialogOpen, setIsUnsavedChangesDialogOpen] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | -1 | null>(null);
  const [pendingNavigationOptions, setPendingNavigationOptions] = useState<
    NavigationOptions | undefined
  >(undefined);
  const [saveBeforeNavigateError, setSaveBeforeNavigateError] = useState<string | null>(null);

  // Block TanStack Router navigation when there are unsaved changes
  const blocker = useBlocker({
    condition: hasUnsavedChanges,
  });

  const isDialogOpen = isUnsavedChangesDialogOpen || blocker.status === 'blocked';

  // Guard browser navigation (back, refresh, close tab)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Execute navigation without checks
  const executeNavigation = useCallback(
    (destination: string | -1, options?: NavigationOptions) => {
      markFastReturnIfNeeded(destination, options);

      if (destination === -1) {
        navigate({ to: '..' });
      } else {
        // Build navigation options dynamically
        // Type assertion needed: TanStack Router expects specific types for search/state,
        // but our navigation wrapper uses dynamic options that can't be validated at compile-time
        const navOptions: any = { to: destination };
        if (options?.search) {
          navOptions.search = options.search;
        }
        if (options?.state) {
          navOptions.state = options.state;
        }
        navigate(navOptions);
      }
    },
    [navigate, markFastReturnIfNeeded]
  );

  // Request navigation with guard check
  const requestNavigation = useCallback(
    (destination: string | -1, options?: NavigationOptions) => {
      if (isDialogOpen) return;

      if (hasUnsavedChanges) {
        setPendingNavigation(destination);
        setPendingNavigationOptions(options);
        setIsUnsavedChangesDialogOpen(true);
      } else {
        executeNavigation(destination, options);
      }
    },
    [hasUnsavedChanges, isDialogOpen, executeNavigation]
  );

  // Handle "Save Changes" button
  const handleSaveAndNavigate = useCallback(async () => {
    setSaveBeforeNavigateError(null);

    try {
      await onBatchSave();

      setIsUnsavedChangesDialogOpen(false);
      setPendingNavigation(null);
      setPendingNavigationOptions(undefined);

      if (blocker.status === 'blocked' && blocker.proceed) {
        markFastReturnIfNeeded(pendingNavigation ?? -1, pendingNavigationOptions);
        blocker.proceed();
      } else if (pendingNavigation) {
        executeNavigation(pendingNavigation, pendingNavigationOptions);
      }
    } catch (error: unknown) {
      let message = 'Failed to save changes. Please try again.';

      if (error instanceof Error) {
        message = error.message;
      } else if (typeof error === 'object' && error !== null && 'data' in error) {
        // RTK Query rejection shape: { data: { error: string } }
        const { data } = error;
        if (
          typeof data === 'object' &&
          data !== null &&
          'error' in data &&
          typeof data.error === 'string'
        ) {
          message = data.error;
        }
      }

      setSaveBeforeNavigateError(message);
    }
  }, [
    pendingNavigation,
    pendingNavigationOptions,
    onBatchSave,
    executeNavigation,
    blocker,
    markFastReturnIfNeeded,
  ]);

  // Handle "Discard Changes" button
  const handleDiscardAndNavigate = useCallback(() => {
    onCancelChanges();

    setIsUnsavedChangesDialogOpen(false);
    setPendingNavigation(null);
    setPendingNavigationOptions(undefined);
    setSaveBeforeNavigateError(null);

    if (blocker.status === 'blocked' && blocker.proceed) {
      markFastReturnIfNeeded(pendingNavigation ?? -1, pendingNavigationOptions);
      blocker.proceed();
    } else if (pendingNavigation) {
      executeNavigation(pendingNavigation, pendingNavigationOptions);
    }
  }, [
    pendingNavigation,
    pendingNavigationOptions,
    onCancelChanges,
    executeNavigation,
    blocker,
    markFastReturnIfNeeded,
  ]);

  // Handle "Cancel" button
  const handleCancelNavigation = useCallback(() => {
    setIsUnsavedChangesDialogOpen(false);
    setPendingNavigation(null);
    setPendingNavigationOptions(undefined);
    setSaveBeforeNavigateError(null);

    if (blocker.status === 'blocked' && blocker.reset) {
      blocker.reset();
    }
  }, [blocker]);

  return {
    isUnsavedChangesDialogOpen: isDialogOpen,
    setIsUnsavedChangesDialogOpen,
    pendingNavigation,
    setPendingNavigation,
    saveBeforeNavigateError,
    setSaveBeforeNavigateError,
    requestNavigation,
    handleSaveAndNavigate,
    handleDiscardAndNavigate,
    handleCancelNavigation,
  };
};
